import React, { useState, useEffect, useContext, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Chart as ChartJS, ArcElement, Tooltip } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useNavigate } from 'react-router-dom';
import ApiURL from '../Api/Api';
import { fetchJson } from '../utils/http';
import Navbar from './Navbar';
import { SidebarContext } from './SidebarContext';
import Sidebar from './Sidebar';
import {
  UserGroupIcon,
  MagnifyingGlassIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ArrowPathIcon,
  ScaleIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentArrowDownIcon,
  PencilSquareIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  AdjustmentsHorizontalIcon,
  ChartPieIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  BanknotesIcon,
  FunnelIcon,
  CalendarDaysIcon,
} from '@heroicons/react/24/outline';
import TransactionImageField from './TransactionImageField';
import { fetchTransactionPhotoRequired, imagePayload } from '../utils/transactionPhoto';
import TransactionProofImage from './TransactionProofImage';

ChartJS.register(ArcElement, Tooltip, ChartDataLabels);

/* ── helpers ─────────────────────────────────────────────────── */
const fmt = (v) =>
  v != null
    ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(v))
    : '₹0';

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const fmtDateTime = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

/* ── TypeBadge ───────────────────────────────────────────────── */
const TypeBadge = ({ type }) =>
  type === 'credit' ? (
    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] font-bold rounded-full border border-emerald-200">
      <ArrowTrendingUpIcon className="w-2.5 h-2.5" /> Credit
    </span>
  ) : type === 'return' ? (
    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-amber-50 text-amber-700 text-[10px] font-bold rounded-full border border-amber-200">
      <ArrowPathIcon className="w-2.5 h-2.5" /> Return
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 bg-red-50 text-red-700 text-[10px] font-bold rounded-full border border-red-200">
      <ArrowTrendingDownIcon className="w-2.5 h-2.5" /> Debit
    </span>
  );

/* ── StatusBadge ─────────────────────────────────────────────── */
const StatusBadge = ({ status }) => {
  const raw = status || 'RECEIVED';
  const s = String(raw).toUpperCase();
  const map = {
    RECEIVED:         'bg-slate-50 text-slate-600 border-slate-200',
    PENDING:          'bg-amber-50 text-amber-700 border-amber-200',
    REQUESTAPPROVED:  'bg-emerald-50 text-emerald-700 border-emerald-200',
    'REQUEST APPROVED': 'bg-emerald-50 text-emerald-700 border-emerald-200',
    RETURN:           'bg-orange-50 text-orange-700 border-orange-200',
    SUBMITTED:        'bg-blue-50 text-blue-700 border-blue-200',
    SUBMIT:           'bg-blue-50 text-blue-700 border-blue-200',
    SAVE:             'bg-indigo-50 text-indigo-700 border-indigo-200',
    APPROVED:         'bg-emerald-50 text-emerald-700 border-emerald-200',
  };
  const label = s === 'REQUESTAPPROVED' || s === 'REQUEST APPROVED' ? 'APPROVED' : s;
  return (
    <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${map[s] || map.RECEIVED}`}>{label}</span>
  );
};

/* ── Stat pill ───────────────────────────────────────────────── */
const Stat = ({ label, value, iconBg, icon: Icon, textColor }) => (
  <div className="flex items-center gap-2.5 bg-white rounded-2xl border border-slate-100 shadow-sm px-3.5 py-3">
    <div className={`${iconBg} w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0`}>
      <Icon className="w-4 h-4 text-white" />
    </div>
    <div className="min-w-0">
      <p className={`text-sm font-bold ${textColor} leading-tight truncate`}>{value}</p>
      <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider mt-0.5">{label}</p>
    </div>
  </div>
);

/* ── Donut card ──────────────────────────────────────────────── */
const DonutCard = ({ title, data, options }) => (
  <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3">{title}</p>
    <div className="h-28">
      <Pie data={data} options={options} />
    </div>
  </div>
);

/* ── Label + input wrapper ───────────────────────────────────── */
const FieldLabel = ({ children }) => (
  <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">{children}</label>
);

const inputCls = 'w-full h-9 px-3 text-[12px] font-medium text-slate-700 bg-slate-50 border border-slate-200 rounded-xl outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 transition-all';
const selectCls = inputCls + ' appearance-none cursor-pointer';

/* ── Sort header cell ────────────────────────────────────────── */
const SortTh = ({ label, col, sortConfig, onSort }) => (
  <th
    className="px-3 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap cursor-pointer hover:text-slate-600 select-none"
    onClick={() => onSort(col)}
  >
    <span className="flex items-center gap-1">
      {label}
      {sortConfig.key === col
        ? sortConfig.direction === 'asc'
          ? <ChevronUpIcon className="w-3 h-3" />
          : <ChevronDownIcon className="w-3 h-3" />
        : <ChevronUpDownIcon className="w-3 h-3 opacity-30" />
      }
    </span>
  </th>
);

/* ══════════════════════════════════════════════════════════════ */
const SeeUserTransaction = ({ entityRole = 'worker' }) => {
  const [workers, setWorkers]               = useState([]);
  const [selectedWorker, setSelectedWorker] = useState({ user_id: '', user_name: '' });
  const [searchQuery, setSearchQuery]       = useState('');
  const [transactions, setTransactions]     = useState([]);
  const [chartData, setChartData]           = useState({
    totalCredit: 0, totalDebit: 0, totalReturn: 0,
    totalCredit1: 0, totalDebit1: 0, totalReturn1: 0,
    totalCredit2: 0, totalDebit2: 0, totalReturn2: 0,
    netBalance: 0, loading: true, error: null,
  });
  const [isLoading, setIsLoading]           = useState(false);
  const [error, setError]                   = useState(null);
  const [activeDataset, setActiveDataset]   = useState('both');
  const [editingTransaction, setEditingTxn] = useState(null);
  const [isModalOpen, setIsModalOpen]       = useState(false);
  const [globalThreshold, setGlobalThreshold] = useState(0);
  const [thresholdForm, setThresholdForm]   = useState({ type: 'global', threshold: '', user_id: '' });
  const [thresholdError, setThresholdError] = useState(null);
  const [thresholdOpen, setThresholdOpen]   = useState(false);
  const [sortConfig, setSortConfig]         = useState({ key: 'created_at', direction: 'desc' });
  const [txnSearch, setTxnSearch]           = useState('');
  const [filterOpen, setFilterOpen]         = useState(false);
  const [dateRange, setDateRange]           = useState({ startDate: '', endDate: '' });
  const [typeFilter, setTypeFilter]         = useState('all');
  const [transactionImage, setTransactionImage] = useState(null);
  const [photoRequired, setPhotoRequired] = useState(false);

  const admin_id    = localStorage.getItem('id');
  const role        = localStorage.getItem('role');
  const currentUser = localStorage.getItem('username') || localStorage.getItem('user_name') || 'Admin';
  const navigate    = useNavigate();
  const { isSidebarOpen, screenWidth } = useContext(SidebarContext);

  const entityLabel       = entityRole === 'admin' ? 'Admin' : 'Worker';
  const entityPluralLabel = entityRole === 'admin' ? 'Admins' : 'Workers';
  const showThreshold     = true;

  /* ── fetchers ─────────────────────────────────────────── */
  const fetchWorkers = async () => {
    try {
      const data = await fetchJson(`${ApiURL}/getUsersWithBalance.php?admin_id=${admin_id}&role=${role}&entity_role=${entityRole}`);
      if (data.status === 'success') {
        setWorkers((data.users || []).filter(u => u.role === entityRole));
        setGlobalThreshold(data.global_threshold || 0);
      } else setError(data.message);
    } catch (e) { setError(`Failed to fetch ${entityPluralLabel.toLowerCase()}: ` + e.message); }
  };

  const fetchTransactions = async () => {
    if (!selectedWorker.user_id) return;
    setIsLoading(true);
    try {
      let url = `${ApiURL}/getUserTransaction.php?admin_id=${admin_id}&role=${role}&worker_id=${selectedWorker.user_id}&worker_user_name=${selectedWorker.user_name}`;
      if (searchQuery) url += `&search_query=${encodeURIComponent(searchQuery)}`;
      if (dateRange.startDate) url += `&start_date=${dateRange.startDate}`;
      if (dateRange.endDate) url += `&end_date=${dateRange.endDate}`;
      const data = await fetchJson(url);
      if (data.status === 'success') { setTransactions(data.transactions || []); setError(null); }
      else setError(data.message);
    } catch (e) { setError('Failed to fetch transactions: ' + e.message); }
    finally { setIsLoading(false); }
  };

  const fetchChartData = async () => {
    if (!selectedWorker.user_id) return;
    try {
      const data = await fetchJson(`${ApiURL}/PieChart.php?user_id=${selectedWorker.user_id}&user_name=${selectedWorker.user_name}`);
      if (data.error) throw new Error(data.error);
      setChartData({
        totalCredit:  Number(data?.total?.credit ?? 0),  totalDebit:  Number(data?.total?.debit ?? 0),  totalReturn:  Number(data?.total?.return ?? 0),
        totalCredit1: Number(data?.save?.credit ?? 0),   totalDebit1: Number(data?.save?.debit ?? 0),   totalReturn1: Number(data?.save?.return ?? 0),
        totalCredit2: Number(data?.submit?.credit ?? 0), totalDebit2: Number(data?.submit?.debit ?? 0), totalReturn2: Number(data?.submit?.return ?? 0),
        netBalance: Number(data?.total?.net_balance ?? 0), loading: false, error: null,
      });
    } catch (e) { setChartData(p => ({ ...p, loading: false, error: e.message })); }
  };

  const updateTransaction = async (txn) => {
    try {
      const safeRemarks = String(txn.remarks || '').trim() || '-';
      if (photoRequired && !transactionImage?.dataUrl && !txn.attachment_path) {
        setError('Proof image is required by company setting');
        return;
      }
      const nowIso = new Date().toISOString();
      const data = await fetchJson(`${ApiURL}/editTransaction.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_no: txn.transaction_no,
          user_id: admin_id,
          transaction_date: txn.transaction_date,
          amount_type: txn.amount_type,
          creditAmount: Number(txn.credit_amount || 0),
          debitAmount: Number(txn.debit_amount || 0),
          particuler_to: txn.particuler_to || '',
          site: txn.site || '',
          main_head: txn.main_head || '',
          sub_head: txn.sub_head || '',
          remarks: safeRemarks,
          form_status: txn.form_status || '',
          updated_by: currentUser,
          attachment_image: imagePayload(transactionImage),
        }),
      });
      if (data.status === 'success') {
        await fetchTransactions();
        await fetchChartData();
        setTransactions(p => p.map(t => t.transaction_no === txn.transaction_no ? { ...t, ...txn, remarks: safeRemarks, updatedBy: currentUser, updatedDate: nowIso } : t));
        setIsModalOpen(false); setEditingTxn(null); setTransactionImage(null);
        setError(null);
      } else setError(data.message);
    } catch (e) { setError('Failed to update: ' + e.message); }
  };

  const setThresholdAPI = async (e) => {
    e.preventDefault();
    if (thresholdForm.threshold < 0) { setThresholdError('Threshold cannot be negative'); return; }
    try {
      const data = await fetchJson(`${ApiURL}/setThreshold.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_id, role, entity_role: entityRole, type: thresholdForm.type, threshold: parseFloat(thresholdForm.threshold), user_id: thresholdForm.type === 'user' ? parseInt(thresholdForm.user_id) : null }),
      });
      if (data.status === 'success') { await fetchWorkers(); setThresholdForm({ type: 'global', threshold: '', user_id: '' }); setThresholdError(null); setThresholdOpen(false); }
      else setThresholdError(data.message);
    } catch (e) { setThresholdError('Failed: ' + e.message); }
  };

  const exportCSV = (rows, filename, headers, mapper) => {
    if (!rows.length) return;
    const csv = [headers, ...rows.map(mapper)].map(r => r.map(c => { const v = String(c ?? ''); return v.includes(',') || v.includes('"') ? `"${v.replace(/"/g,'""')}"` : v; }).join(',')).join('\n');
    const a = document.createElement('a'); a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' })); a.download = filename; document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const exportWorkers = () => exportCSV(workers, `${entityPluralLabel.toLowerCase()}.csv`,
    ['User Name','Credit','Debit','Return','Net Balance','Threshold'],
    w => [w.user_name, w.total_credit, w.total_debit, w.total_return, w.net_balance, w.threshold ?? globalThreshold]);

  const exportTransactions = () => exportCSV(transactions, `txn_${selectedWorker.user_name}.csv`,
    ['#','Date','Type','Credit','Debit','Return','Balance','Particulars','Site','Project','Main Head','Sub Head','Remarks','Status','Created By','Updated By','Updated At'],
    (t, i) => [i+1, t.transaction_date, t.amount_type, t.credit_amount, t.debit_amount, t.return_amount, t.net_balance, t.particuler_to||'', t.site||'', t.project_name||'', t.main_head||'', t.sub_head||'', t.remarks||'', t.form_status||'', t.created_by||'', t.updatedBy || t.updated_by || '', t.updatedDate || t.updated_at || '']);

  const handleSort = (key) => setSortConfig(p => ({ key, direction: p.key === key && p.direction === 'asc' ? 'desc' : 'asc' }));

  useEffect(() => {
    if (!['admin','company_owner'].includes(role) || !admin_id) { setError('Access denied'); return; }
    fetchWorkers();
    fetchTransactionPhotoRequired(admin_id).then(setPhotoRequired).catch(() => {});
  }, []);
  useEffect(() => { fetchTransactions(); fetchChartData(); }, [selectedWorker, searchQuery, dateRange.startDate, dateRange.endDate]);

  /* ── derived ──────────────────────────────────────────────── */
  const filteredWorkers = useMemo(() =>
    workers.filter(w => w.user_name.toLowerCase().includes(searchQuery.toLowerCase())),
    [workers, searchQuery]);

  const sortedTxns = useMemo(() => {
    let list = transactions.filter(t => {
      if (typeFilter !== 'all' && t.amount_type !== typeFilter) return false;
      if (!txnSearch) return true;
      const q = txnSearch.toLowerCase();
      return (t.particuler_to||'').toLowerCase().includes(q) || (t.site||'').toLowerCase().includes(q) || (t.remarks||'').toLowerCase().includes(q) || (t.main_head||'').toLowerCase().includes(q);
    });
    return [...list].sort((a, b) => {
      if (sortConfig.key === 'created_at' || sortConfig.key === 'transaction_date') {
        const da = new Date(a[sortConfig.key]), db = new Date(b[sortConfig.key]);
        return sortConfig.direction === 'asc' ? da - db : db - da;
      }
      if (sortConfig.key === 'amount_type') return sortConfig.direction === 'asc' ? a.amount_type.localeCompare(b.amount_type) : b.amount_type.localeCompare(a.amount_type);
      return 0;
    });
  }, [transactions, sortConfig, txnSearch, typeFilter]);

  const hasActiveFilter = dateRange.startDate || dateRange.endDate || typeFilter !== 'all' || txnSearch;

  /* ── chart helpers ────────────────────────────────────────── */
  const getPieData = (credit, debit, ret) => {
    const map = { credit: [credit, ['#22c55e']], debit: [debit, ['#ef4444']], return: [ret, ['#f59e0b']], both: [[credit,debit,ret], ['#22c55e','#ef4444','#f59e0b']] };
    const [d, bg] = map[activeDataset] || map.both;
    const labels = activeDataset === 'both' ? ['Credit','Debit','Return'] : [activeDataset.charAt(0).toUpperCase()+activeDataset.slice(1)];
    return { labels, datasets: [{ data: Array.isArray(d) ? d : [d], backgroundColor: bg, borderWidth: 0 }] };
  };

  const pieOpts = {
    responsive: true, maintainAspectRatio: false, cutout: '58%',
    plugins: {
      legend: { display: false },
      tooltip: { backgroundColor: 'rgba(15,23,42,0.85)', padding: 10, callbacks: { label: ctx => { const t = ctx.dataset.data.reduce((a,b)=>a+b,0); const pct = t > 0 ? Math.round(ctx.raw/t*100) : 0; return ` ${ctx.label}: ${fmt(ctx.raw)} (${pct}%)`; } } },
      datalabels: { display: false },
    },
  };

  /* ── selected worker's threshold alert ───────────────────── */
  const selectedWorkerData = workers.find(w => w.user_id === selectedWorker.user_id);
  const threshold = selectedWorkerData?.threshold ?? globalThreshold;
  const isLowBalance = showThreshold && selectedWorkerData && selectedWorkerData.net_balance <= threshold;

  return (
    <div className="min-h-screen bg-[#f0f2f8]">
      <Navbar />
      <div className="flex pt-16">
        <Sidebar />
        <motion.main
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
          className={`flex-1 min-h-screen transition-all duration-300 ${isSidebarOpen ? 'lg:ml-72' : ''}`}
        >
          <div className="max-w-7xl mx-auto px-3 py-4 sm:px-5 sm:py-5 lg:px-6 lg:py-6">

            {/* ── Page header ──────────────────────────────────── */}
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                {selectedWorker.user_id && (
                  <button onClick={() => setSelectedWorker({ user_id: '', user_name: '' })}
                    className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-indigo-600 transition-all shadow-sm sm:hidden">
                    <ArrowLeftIcon className="w-4 h-4" />
                  </button>
                )}
                <div>
                  <h1 className="text-lg font-bold text-slate-800 leading-tight">
                    {selectedWorker.user_id ? `${selectedWorker.user_name}` : `${entityLabel} Report`}
                  </h1>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    {selectedWorker.user_id ? `Transaction details` : `${filteredWorkers.length} ${entityPluralLabel.toLowerCase()}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {selectedWorker.user_id && (
                  <button
                    onClick={() => navigate(`/Edit?user_id=${selectedWorker.user_id}&user_name=${encodeURIComponent(selectedWorker.user_name)}`)}
                    className="flex items-center gap-1.5 h-8 px-3 rounded-xl bg-indigo-600 text-white text-xs font-semibold shadow-sm transition-all hover:bg-indigo-700"
                  >
                    <PencilSquareIcon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Open Edit</span>
                  </button>
                )}
                {showThreshold && (
                  <button onClick={() => setThresholdOpen(p => !p)}
                    className={`flex items-center gap-1.5 h-8 px-3 rounded-xl border text-xs font-semibold transition-all shadow-sm ${thresholdOpen ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                    <AdjustmentsHorizontalIcon className="w-3.5 h-3.5" />
                    <span className="hidden sm:inline">Threshold</span>
                  </button>
                )}
                <button onClick={() => setFilterOpen(p => !p)}
                  className={`flex items-center gap-1.5 h-8 px-3 rounded-xl border text-xs font-semibold transition-all shadow-sm ${hasActiveFilter ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}>
                  <FunnelIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Filter</span>
                </button>
                <button onClick={selectedWorker.user_id ? exportTransactions : exportWorkers}
                  disabled={selectedWorker.user_id ? !transactions.length : !workers.length}
                  className="flex items-center gap-1.5 h-8 px-3 rounded-xl bg-white border border-slate-200 text-slate-600 text-xs font-semibold hover:border-slate-300 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-all">
                  <DocumentArrowDownIcon className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Export</span>
                </button>
                <button onClick={fetchWorkers}
                  className="w-8 h-8 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-500 hover:text-indigo-600 shadow-sm transition-all">
                  <ArrowPathIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* ── Threshold panel ──────────────────────────────── */}
            <AnimatePresence>
              {thresholdOpen && showThreshold && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden mb-4">
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Set Threshold</p>
                      <p className="text-[11px] text-slate-400">Global: <span className="font-bold text-slate-700">{fmt(globalThreshold)}</span></p>
                    </div>
                    <form onSubmit={setThresholdAPI}>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <div>
                          <FieldLabel>Type</FieldLabel>
                          <select name="type" value={thresholdForm.type} onChange={e => setThresholdForm(p=>({...p,type:e.target.value}))}
                            className={selectCls}>
                            <option value="global">Global</option>
                            <option value="user">{entityLabel}</option>
                          </select>
                        </div>
                        {thresholdForm.type === 'user' && (
                          <div>
                            <FieldLabel>{entityLabel}</FieldLabel>
                            <select name="user_id" value={thresholdForm.user_id} onChange={e => setThresholdForm(p=>({...p,user_id:e.target.value}))}
                              className={selectCls}>
                              <option value="">Select</option>
                              {workers.map(w => <option key={w.user_id} value={w.user_id}>{w.user_name}</option>)}
                            </select>
                          </div>
                        )}
                        <div>
                          <FieldLabel>Amount (₹)</FieldLabel>
                          <input type="number" name="threshold" value={thresholdForm.threshold}
                            onChange={e => setThresholdForm(p=>({...p,threshold:e.target.value}))}
                            className={inputCls} min="0" step="0.01" placeholder="0" required />
                        </div>
                        <div className="flex items-end">
                          <button type="submit"
                            className="w-full h-9 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-colors">
                            Save Threshold
                          </button>
                        </div>
                      </div>
                      {thresholdError && <p className="text-xs text-red-500 mt-2">{thresholdError}</p>}
                    </form>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Filter panel */}
            <AnimatePresence>
              {filterOpen && selectedWorker.user_id && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
                  className="overflow-hidden mb-4">
                  <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest">Filters</p>
                      {hasActiveFilter && (
                        <button onClick={() => { setDateRange({ startDate: '', endDate: '' }); setTypeFilter('all'); setTxnSearch(''); }}
                          className="text-[11px] text-rose-500 font-semibold hover:text-rose-600 touch-manipulation">Clear all</button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2 grid grid-cols-2 gap-2">
                        <div>
                          <FieldLabel>From</FieldLabel>
                          <div className="relative">
                            <CalendarDaysIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                            <input type="date" value={dateRange.startDate}
                              onChange={e => setDateRange(p => ({ ...p, startDate: e.target.value }))}
                              className={inputCls} />
                          </div>
                        </div>
                        <div>
                          <FieldLabel>To</FieldLabel>
                          <div className="relative">
                            <CalendarDaysIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                            <input type="date" value={dateRange.endDate}
                              onChange={e => setDateRange(p => ({ ...p, endDate: e.target.value }))}
                              className={inputCls} />
                          </div>
                        </div>
                      </div>
                      <div>
                        <FieldLabel>Type</FieldLabel>
                        <div className="flex gap-1 h-9 bg-slate-50 border border-slate-200 rounded-xl p-1">
                          {['all','credit','debit','return'].map(v => (
                            <button key={v} onClick={() => setTypeFilter(v)}
                              className={`flex-1 text-[11px] font-bold rounded-lg capitalize transition-all ${
                                typeFilter === v
                                  ? v === 'credit' ? 'bg-emerald-500 text-white shadow-sm'
                                  : v === 'debit' ? 'bg-red-500 text-white shadow-sm'
                                  : v === 'return' ? 'bg-amber-500 text-white shadow-sm'
                                  : 'bg-indigo-600 text-white shadow-sm'
                                  : 'text-slate-500 hover:text-slate-700'
                              }`}>{v}</button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3">
                      <FieldLabel>Search</FieldLabel>
                      <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                        <input type="text" value={txnSearch} onChange={e => setTxnSearch(e.target.value)}
                          placeholder="Search by party, site, head, remarks…"
                          className={`${inputCls} pl-9`} />
                        {txnSearch && (
                          <button onClick={() => setTxnSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2">
                            <XMarkIcon className="w-3.5 h-3.5 text-slate-400 hover:text-slate-600" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* ── Error ────────────────────────────────────────── */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl px-4 py-3">{error}</div>
            )}

            {/* ══ VIEW A: No worker selected — summary table ══ */}
            {!selectedWorker.user_id ? (
              <div>
                {/* Search + select row */}
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                  <div className="relative flex-1">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-300 pointer-events-none" />
                    <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                      placeholder={`Search ${entityPluralLabel.toLowerCase()}…`}
                      className={`${inputCls} pl-9`} />
                  </div>
                </div>

                {/* ── MOBILE: worker cards ─────────────────────── */}
                <div className="block sm:hidden space-y-2">
                  {filteredWorkers.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-100 py-12 flex flex-col items-center gap-2 shadow-sm">
                      <UserGroupIcon className="w-10 h-10 text-slate-200" />
                      <p className="text-sm text-slate-400">No {entityPluralLabel.toLowerCase()} found</p>
                    </div>
                  ) : filteredWorkers.map(w => {
                    const thr = w.threshold ?? globalThreshold;
                    const low = showThreshold && w.net_balance <= thr;
                    return (
                      <motion.div key={w.user_id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                        onClick={() => setSelectedWorker({ user_id: w.user_id, user_name: w.user_name })}
                        className={`bg-white rounded-2xl border shadow-sm overflow-hidden cursor-pointer active:scale-[0.99] transition-transform ${low ? 'border-red-200' : 'border-slate-100'}`}>
                        {low && <div className="h-0.5 bg-red-400 w-full" />}
                        <div className="px-4 py-3.5 flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 font-bold text-sm ${low ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600'}`}>
                              {w.user_name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="text-[13px] font-bold text-slate-800">{w.user_name}</p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-[10px] text-emerald-600 font-semibold">{fmt(w.total_credit)} Cr</span>
                                <span className="text-[10px] text-red-500 font-semibold">{fmt(w.total_debit)} Dr</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right flex items-center gap-2">
                            <div>
                              <p className={`text-sm font-bold ${w.net_balance >= 0 ? 'text-slate-800' : 'text-red-600'}`}>{fmt(w.net_balance)}</p>
                              {showThreshold && <p className="text-[10px] text-slate-400">Thr: {fmt(thr)}</p>}
                            </div>
                            <ChevronRightIcon className="w-4 h-4 text-slate-300" />
                          </div>
                        </div>
                        {low && (
                          <div className="px-4 pb-3">
                            <div className="flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-xl px-3 py-1.5">
                              <ExclamationTriangleIcon className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
                              <p className="text-[10px] text-red-600 font-semibold">Balance at or below threshold</p>
                            </div>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                </div>

                {/* ── DESKTOP: summary table ────────────────────── */}
                <div className="hidden sm:block bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[12px] text-left border-collapse min-w-[560px]">
                      <thead>
                        <tr className="border-b border-slate-100 bg-slate-50/80">
                          {['User Name','Credit','Debit','Return','Net Balance', ...(showThreshold ? ['Threshold'] : [])].map(h => (
                            <th key={h} className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{h}</th>
                          ))}
                          <th className="px-4 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-50">
                        {filteredWorkers.length === 0 ? (
                          <tr><td colSpan={7} className="text-center py-12 text-sm text-slate-400">No {entityPluralLabel.toLowerCase()} found</td></tr>
                        ) : filteredWorkers.map((w, i) => {
                          const thr = w.threshold ?? globalThreshold;
                          const low = showThreshold && w.net_balance <= thr;
                          return (
                            <motion.tr key={w.user_id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.02 }}
                              className={`hover:bg-slate-50/60 transition-colors cursor-pointer ${low ? 'bg-red-50/30' : ''}`}
                              onClick={() => setSelectedWorker({ user_id: w.user_id, user_name: w.user_name })}>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-2.5">
                                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center font-bold text-xs flex-shrink-0 ${low ? 'bg-red-100 text-red-600' : 'bg-indigo-50 text-indigo-600'}`}>
                                    {w.user_name.charAt(0).toUpperCase()}
                                  </div>
                                  <span className="font-semibold text-slate-800">{w.user_name}</span>
                                  {low && <ExclamationTriangleIcon className="w-3.5 h-3.5 text-red-500" />}
                                </div>
                               </td>
                              <td className="px-4 py-3 text-emerald-600 font-semibold">{fmt(w.total_credit)}</td>
                              <td className="px-4 py-3 text-red-600 font-semibold">{fmt(w.total_debit)}</td>
                              <td className="px-4 py-3 text-amber-600 font-semibold">{fmt(w.total_return)}</td>
                              <td className="px-4 py-3 font-bold text-slate-800">{fmt(w.net_balance)}</td>
                              {showThreshold && <td className="px-4 py-3 text-slate-500">{fmt(thr)}</td>}
                              <td className="px-4 py-3">
                                <span className="text-[11px] font-bold text-indigo-600 flex items-center gap-1 hover:text-indigo-700">
                                  View <ChevronRightIcon className="w-3 h-3" />
                                </span>
                               </td>
                            </motion.tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40">
                    <p className="text-[11px] text-slate-400">
                      {filteredWorkers.length} {entityPluralLabel.toLowerCase()} · Global threshold: <span className="font-bold text-slate-600">{fmt(globalThreshold)}</span>
                    </p>
                  </div>
                </div>
              </div>

            /* ══ VIEW B: Worker selected ══════════════════════ */
            ) : (
              <div>
                {/* Low balance alert */}
                <AnimatePresence>
                  {isLowBalance && (
                    <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                      className="mb-4 flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                      <ExclamationTriangleIcon className="w-4 h-4 text-red-500 flex-shrink-0" />
                      <p className="text-xs font-semibold text-red-700">
                        Balance <span className="font-bold">{fmt(selectedWorkerData.net_balance)}</span> is at or below threshold <span className="font-bold">{fmt(threshold)}</span>
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Back button — desktop */}
                <button onClick={() => setSelectedWorker({ user_id: '', user_name: '' })}
                  className="hidden sm:flex items-center gap-1.5 text-[11px] font-bold text-slate-500 hover:text-indigo-600 mb-4 transition-colors">
                  <ArrowLeftIcon className="w-3.5 h-3.5" /> Back to all {entityPluralLabel.toLowerCase()}
                </button>

                {/* Stat pills */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4">
                  <Stat label="Credit" value={fmt(chartData.totalCredit)} icon={ArrowTrendingUpIcon} iconBg="bg-emerald-500" textColor="text-emerald-700" />
                  <Stat label="Debit"  value={fmt(chartData.totalDebit)}  icon={ArrowTrendingDownIcon} iconBg="bg-red-500" textColor="text-red-700" />
                  <Stat label="Return" value={fmt(chartData.totalReturn)} icon={ArrowPathIcon} iconBg="bg-amber-500" textColor="text-amber-700" />
                  <Stat label="Balance" value={fmt(chartData.netBalance)} icon={ScaleIcon}
                    iconBg={chartData.netBalance >= 0 ? 'bg-sky-500' : 'bg-orange-500'}
                    textColor={chartData.netBalance >= 0 ? 'text-sky-700' : 'text-orange-700'} />
                </div>

                {/* Chart section */}
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 mb-4">
                  <div className="flex items-center justify-between mb-4">
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                      <ChartPieIcon className="w-3.5 h-3.5" /> Transaction Breakdown
                    </p>
                    <div className="flex gap-1 bg-slate-50 border border-slate-200 rounded-xl p-1">
                      {['both','credit','debit','return'].map(v => (
                        <button key={v} onClick={() => setActiveDataset(v)}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-bold capitalize transition-all ${
                            activeDataset === v
                              ? v === 'credit' ? 'bg-emerald-500 text-white shadow-sm'
                              : v === 'debit'  ? 'bg-red-500 text-white shadow-sm'
                              : v === 'return' ? 'bg-amber-500 text-white shadow-sm'
                              : 'bg-indigo-600 text-white shadow-sm'
                              : 'text-slate-500 hover:text-slate-700'
                          }`}>
                          {v === 'both' ? 'All' : v}
                        </button>
                      ))}
                    </div>
                  </div>
                  {chartData.loading ? (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      {[1,2,3,4].map(i => <div key={i} className="h-36 bg-slate-100 rounded-xl animate-pulse" />)}
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <DonutCard title="All" data={getPieData(chartData.totalCredit, chartData.totalDebit, chartData.totalReturn)} options={pieOpts} />
                      <DonutCard title="Saved" data={getPieData(chartData.totalCredit1, chartData.totalDebit1, chartData.totalReturn1)} options={pieOpts} />
                      <DonutCard title="Submitted" data={getPieData(chartData.totalCredit2, chartData.totalDebit2, chartData.totalReturn2)} options={pieOpts} />
                      <DonutCard title="Net Balance" data={{ labels:[chartData.netBalance>=0?'Surplus':'Deficit'], datasets:[{data:[Math.abs(chartData.netBalance)],backgroundColor:[chartData.netBalance>=0?'#22c55e':'#ef4444'],borderWidth:0}] }} options={pieOpts} />
                    </div>
                  )}
                </div>

                {/* Transaction list header */}
                <div className="flex items-center justify-between mb-3">
                  <p className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Transactions</p>
                  <p className="text-[11px] text-slate-400">{sortedTxns.length} rows</p>
                </div>

                {isLoading ? (
                  <div className="space-y-2">{[1,2,3,4].map(i => <div key={i} className="bg-white rounded-xl h-14 animate-pulse border border-slate-100" />)}</div>
                ) : sortedTxns.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-slate-100 py-14 flex flex-col items-center gap-2 shadow-sm">
                    <BanknotesIcon className="w-10 h-10 text-slate-200" />
                    <p className="text-sm text-slate-400">No transactions found</p>
                  </div>
                ) : (
                  <>
                    {/* ── MOBILE: transaction cards ──────────────── */}
                    <div className="block sm:hidden space-y-2">
                      {sortedTxns.map((txn, i) => (
                        <motion.div key={txn.transaction_no} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: Math.min(i * 0.025, 0.25) }}
                          className={`bg-white rounded-2xl border shadow-sm overflow-hidden cursor-pointer ${txn.updatedDate ? 'border-emerald-200' : 'border-slate-100'}`}
                          onClick={() => { setEditingTxn({...txn}); setTransactionImage(null); setIsModalOpen(true); }}>
                          <div className={`h-0.5 w-full ${txn.amount_type==='credit'?'bg-emerald-400':txn.amount_type==='return'?'bg-amber-400':'bg-red-400'}`} />
                          <div className="px-4 py-3">
                            <div className="flex items-start justify-between gap-2 mb-2">
                              <div>
                                <p className="text-[13px] font-bold text-slate-800">{txn.particuler_to || '—'}</p>
                                <p className="text-[10px] text-slate-400">{fmtDate(txn.transaction_date)}</p>
                              </div>
                              <div className="text-right">
                                <p className={`text-[15px] font-bold ${txn.amount_type==='credit'?'text-emerald-600':txn.amount_type==='return'?'text-amber-600':'text-red-600'}`}>
                                  {txn.amount_type==='credit'?'+':'-'}{fmt(txn.amount_type==='credit'?txn.credit_amount:txn.amount_type==='return'?txn.return_amount:txn.debit_amount)}
                                </p>
                                <p className="text-[10px] text-slate-400">Bal {fmt(txn.net_balance)}</p>
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5 items-center">
                              <TypeBadge type={txn.amount_type} />
                              <StatusBadge status={txn.form_status} />
                              <TransactionProofImage path={txn.attachment_path} />
                              {txn.site && <span className="px-2 py-0.5 bg-slate-50 text-slate-500 text-[10px] font-semibold rounded-full border border-slate-200">📍 {txn.site}</span>}
                              {txn.main_head && <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-semibold rounded-full border border-indigo-100">{txn.main_head}</span>}
                            </div>
                            <div className="mt-2 pt-2 border-t border-slate-50 flex items-center justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-[10px] text-slate-400 truncate">Created: {txn.created_by || '—'}</p>
                                <p className="text-[10px] text-emerald-600 truncate">Updated: {(txn.updatedBy || txn.updated_by || '—')} · {fmtDateTime(txn.updatedDate || txn.updated_at)}</p>
                              </div>
                              <div className="flex items-center gap-1 text-indigo-500">
                                <PencilSquareIcon className="w-3 h-3" />
                                <span className="text-[10px] font-semibold">Edit</span>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    {/* ── DESKTOP: transaction table ─────────────── */}
                    <div className="hidden sm:block bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full text-[12px] text-left border-collapse min-w-[1000px]">
                          <thead>
                            <tr className="border-b border-slate-100 bg-slate-50/80">
                              <th className="px-3 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">#</th>
                              <SortTh label="Date" col="transaction_date" sortConfig={sortConfig} onSort={handleSort} />
                              <SortTh label="Created" col="created_at" sortConfig={sortConfig} onSort={handleSort} />
                              <SortTh label="Type" col="amount_type" sortConfig={sortConfig} onSort={handleSort} />
                              {['Credit','Debit','Return','Balance','Particulars','Site','Project','Head','Remarks','Proof','Status','Created By','Updated By','Updated At'].map(h => (
                                <th key={h} className="px-3 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                              ))}
                              <th className="px-3 py-3 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Edit</th>
                             </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-50">
                            {sortedTxns.map((txn, i) => (
                              <motion.tr key={txn.transaction_no} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                                transition={{ delay: Math.min(i * 0.015, 0.2) }}
                                className={`hover:bg-slate-50/60 transition-colors ${txn.updatedDate ? 'bg-emerald-50/20' : ''}`}>
                                <td className="px-3 py-2.5 text-slate-400">{i+1}</td>
                                <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{fmtDate(txn.transaction_date)}</td>
                                <td className="px-3 py-2.5 text-slate-400 whitespace-nowrap text-[11px]">{fmtDateTime(txn.created_at)}</td>
                                <td className="px-3 py-2.5"><TypeBadge type={txn.amount_type} /></td>
                                <td className="px-3 py-2.5 text-emerald-600 font-semibold">{txn.amount_type==='credit'?fmt(txn.credit_amount):'—'}</td>
                                <td className="px-3 py-2.5 text-red-600 font-semibold">{txn.amount_type==='debit'?fmt(txn.debit_amount):'—'}</td>
                                <td className="px-3 py-2.5 text-amber-600 font-semibold">{txn.amount_type==='return'?fmt(txn.return_amount):'—'}</td>
                                <td className="px-3 py-2.5 font-bold text-slate-800">{fmt(txn.net_balance)}</td>
                                <td className="px-3 py-2.5 text-slate-600 max-w-[100px] truncate">{txn.particuler_to||'—'}</td>
                                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.site||'—'}</td>
                                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.project_name||'—'}</td>
                                <td className="px-3 py-2.5">
                                  {txn.main_head ? <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[10px] font-semibold rounded-full border border-indigo-100 whitespace-nowrap">{txn.main_head}</span> : '—'}
                                </td>
                                <td className="px-3 py-2.5 text-slate-400 max-w-[80px] truncate">{txn.remarks||'—'}</td>
                                <td className="px-3 py-2.5"><TransactionProofImage path={txn.attachment_path} /></td>
                                <td className="px-3 py-2.5"><StatusBadge status={txn.form_status} /></td>
                                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.created_by||'—'}</td>
                                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap">{txn.updatedBy || txn.updated_by || '—'}</td>
                                <td className="px-3 py-2.5 text-slate-500 whitespace-nowrap text-[11px]">{fmtDateTime(txn.updatedDate || txn.updated_at)}</td>
                                <td className="px-3 py-2.5">
                                  <button onClick={() => { setEditingTxn({...txn}); setTransactionImage(null); setIsModalOpen(true); }}
                                    className="w-7 h-7 rounded-lg bg-slate-50 border border-slate-200 flex items-center justify-center text-slate-400 hover:text-indigo-600 hover:border-indigo-200 hover:bg-indigo-50 transition-all">
                                    <PencilSquareIcon className="w-3.5 h-3.5" />
                                  </button>
                                 </td>
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50/40 flex items-center justify-between">
                        <p className="text-[11px] text-slate-400">{sortedTxns.length} transactions</p>
                        <button onClick={exportTransactions} className="flex items-center gap-1.5 text-[11px] font-bold text-indigo-600 hover:text-indigo-700">
                          <DocumentArrowDownIcon className="w-3.5 h-3.5" /> Export CSV
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </motion.main>
      </div>

      {/* Edit modal */}
      <AnimatePresence>
        {isModalOpen && editingTransaction && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-end sm:items-center justify-center z-50 p-0 sm:p-4"
            onClick={e => e.target === e.currentTarget && (setIsModalOpen(false), setEditingTxn(null), setTransactionImage(null))}>
            <motion.div
              initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
              className="bg-white w-full sm:max-w-lg rounded-t-3xl sm:rounded-2xl overflow-hidden shadow-2xl"
            >
              <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                <p className="text-sm font-bold text-slate-800">Edit Transaction</p>
                <button onClick={() => { setIsModalOpen(false); setEditingTxn(null); setTransactionImage(null); }}
                  className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-slate-500 hover:bg-slate-200 transition-colors">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
              <form onSubmit={e => { e.preventDefault(); updateTransaction(editingTransaction); }}
                className="px-5 py-4 overflow-y-auto max-h-[75vh]">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Date', name: 'transaction_date', type: 'date' },
                    { label: 'Particulars', name: 'particuler_to', type: 'text' },
                    { label: 'Site', name: 'site', type: 'text' },
                    { label: 'Main Head', name: 'main_head', type: 'text' },
                    { label: 'Sub Head', name: 'sub_head', type: 'text' },
                    { label: 'Remarks', name: 'remarks', type: 'text' },
                    { label: 'Credit Amount', name: 'credit_amount', type: 'number' },
                    { label: 'Debit Amount',  name: 'debit_amount',  type: 'number' },
                  ].map(({ label, name, type }) => (
                    <div key={name}>
                      <FieldLabel>{label}</FieldLabel>
                      <input type={type} name={name} value={editingTransaction[name] || ''}
                        onChange={e => setEditingTxn(p => ({...p,[name]:e.target.value}))}
                        className={inputCls} min={type==='number'?'0':undefined} step={type==='number'?'0.01':undefined} />
                    </div>
                  ))}
                  <div>
                    <FieldLabel>Type</FieldLabel>
                    <select name="amount_type" value={editingTransaction.amount_type}
                      onChange={e => setEditingTxn(p=>({...p,amount_type:e.target.value}))} className={selectCls}>
                      <option value="credit">Credit</option>
                      <option value="debit">Debit</option>
                      <option value="return">Return</option>
                    </select>
                  </div>
                  <div>
                    <FieldLabel>Status</FieldLabel>
                    <select name="form_status" value={editingTransaction.form_status || 'SAVE'}
                      onChange={e => setEditingTxn(p=>({...p,form_status:e.target.value}))} className={selectCls}>
                      <option value="SAVE">SAVE</option>
                      <option value="SUBMIT">SUBMIT</option>
                      <option value="RECEIVED">RECEIVED</option>
                      <option value="RETURN">RETURN</option>
                      <option value="APPROVED">APPROVED</option>
                    </select>
                  </div>
                </div>
                <div className="mt-4">
                  <TransactionImageField
                    value={transactionImage}
                    onChange={setTransactionImage}
                    required={photoRequired && !editingTransaction?.attachment_path}
                    label="Updated proof image"
                  />
                </div>
                <div className="flex gap-2 mt-5 pt-4 border-t border-slate-100">
                  <button type="button" onClick={() => { setIsModalOpen(false); setEditingTxn(null); setTransactionImage(null); }}
                    className="flex-1 h-10 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors">
                    Cancel
                  </button>
                  <button type="submit"
                    className="flex-1 h-10 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition-colors shadow-sm shadow-indigo-200">
                    Save Changes
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SeeUserTransaction;
